import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref as dbRef, set } from 'firebase/database';
import { ref as storageRef, uploadBytes } from 'firebase/storage';
import { database, storage } from '../../Firebase/firebaseconfig';
import { buildBillPDF } from './billPDF';
import { generateUniqueNumber } from './uniqueNumber';
import { ArrowLeft, Plus, Trash2, Receipt } from 'lucide-react';
import './admin-page.css';

function fmtINR(n) {
  return '₹' + Number(n).toLocaleString('en-IN');
}

const emptyItem = () => ({ itemName: '', quantity: 1, price: '', warrantyYes: false, warrantyValue: '', warrantyUnit: 'months', serialNumberYes: false, serialNumber: '' });

export default function GenerateBill() {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [date, setDate] = useState(new Date().toLocaleDateString('en-IN'));
  const [items, setItems] = useState([emptyItem()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const updateItem = (i, field, val) =>
    setItems(prev => prev.map((it, idx) => idx === i ? { ...it, [field]: val } : it));
  const addItem = () => setItems(prev => [...prev, emptyItem()]);
  const removeItem = (i) => setItems(prev => prev.filter((_, idx) => idx !== i));

  const total = items.reduce((sum, it) => sum + (Number(it.quantity) * Number(it.price) || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!customer.trim() || !phone.trim()) return setError('Customer name and phone are required.');
    if (items.some(it => !it.itemName.trim() || !it.price)) return setError('All items need a name and price.');
    setError('');
    setSaving(true);
    try {
      const invoice = await generateUniqueNumber('bill');
      const billItems = items.map(it => ({
        itemName: it.itemName,
        quantity: Number(it.quantity),
        price: Number(it.price),
        warranty: it.warrantyYes ? `${it.warrantyValue} ${it.warrantyUnit}` : 'No',
        serialNumber: it.serialNumberYes ? it.serialNumber.trim() : '',
      }));

      const pdfBlob = await buildBillPDF(invoice, customer, phone, date, billItems, total, address);
      const filename = `${customer.replace(/\s+/g, '_')}_${date.replace(/\//g, '-')}_${Date.now()}.pdf`;
      await uploadBytes(
        storageRef(storage, `bills/${invoice}/${filename}`),
        pdfBlob,
        { contentType: 'application/pdf' }
      );

      await set(dbRef(database, `bill/${invoice}`), {
        customerName: customer,
        phoneNumber: phone,
        address: address.trim(),
        dateOfPurchase: date,
        totalPrice: total,
        items: billItems,
      });

      navigate(`/admin/dashboard/bill/${invoice}`);
    } catch (err) {
      setError('Failed: ' + err.message);
      setSaving(false);
    }
  };

  return (
    <div className="ap-page ap-page-wide">
      <div className="ap-header">
        <button className="ap-back-btn" onClick={() => navigate('/admin/dashboard')}>
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="ap-title">Generate Bill</h1>
          <p className="ap-subtitle">Create invoice and upload PDF to Firebase</p>
        </div>
      </div>

      {error && <div className="ap-error">{error}</div>}

      <div className="ap-card">
        <form onSubmit={handleSubmit}>
          <p className="ap-section-title">Customer Details</p>
          <div className="ap-row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
            <div className="ap-form-group">
              <label className="ap-label">Customer Name</label>
              <input className="ap-input" value={customer} onChange={e => setCustomer(e.target.value)} placeholder="Full name" />
            </div>
            <div className="ap-form-group">
              <label className="ap-label">Phone Number</label>
              <input className="ap-input" type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="98765 43210" />
            </div>
            <div className="ap-form-group">
              <label className="ap-label">Date of Purchase</label>
              <input className="ap-input" value={date} onChange={e => setDate(e.target.value)} placeholder="DD/MM/YYYY" />
            </div>
          </div>
          <div className="ap-form-group">
            <label className="ap-label">Address <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></label>
            <textarea
              className="ap-input"
              rows={2}
              value={address}
              onChange={e => setAddress(e.target.value)}
              placeholder="Street, area, city…"
              style={{ resize: 'vertical', lineHeight: 1.5 }}
            />
          </div>

          <p className="ap-section-title">Items</p>
          {items.map((it, i) => (
            <div key={i} className="bill-item-block">
              <div className="bill-item-header">
                <span className="bill-item-num">Item {i + 1}</span>
                {items.length > 1 && (
                  <button type="button" className="ap-btn ap-btn-danger" style={{ padding: '4px 10px', fontSize: '0.76rem', display: 'flex', alignItems: 'center', gap: 4 }} onClick={() => removeItem(i)}>
                    <Trash2 size={13} /> Remove
                  </button>
                )}
              </div>
              <div className="ap-row" style={{ gridTemplateColumns: '2fr 1fr 1fr' }}>
                <div className="ap-form-group">
                  <label className="ap-label">Item Name</label>
                  <input className="ap-input" value={it.itemName} onChange={e => updateItem(i, 'itemName', e.target.value)} placeholder="e.g. Kingston 8GB RAM" />
                </div>
                <div className="ap-form-group">
                  <label className="ap-label">Quantity</label>
                  <input className="ap-input" type="number" min="1" value={it.quantity} onChange={e => updateItem(i, 'quantity', e.target.value)} />
                </div>
                <div className="ap-form-group">
                  <label className="ap-label">Price (₹)</label>
                  <input className="ap-input" type="number" value={it.price} onChange={e => updateItem(i, 'price', e.target.value)} placeholder="2500" />
                </div>
              </div>
              <div className="bill-warranty-row">
                <label className="bill-warranty-check">
                  <input type="checkbox" checked={it.warrantyYes} onChange={e => updateItem(i, 'warrantyYes', e.target.checked)} />
                  Warranty
                </label>
                {it.warrantyYes && (
                  <>
                    <input className="ap-input" type="number" min="1" value={it.warrantyValue} onChange={e => updateItem(i, 'warrantyValue', e.target.value)} placeholder="Duration" style={{ width: 90 }} />
                    <select className="ap-select" value={it.warrantyUnit} onChange={e => updateItem(i, 'warrantyUnit', e.target.value)} style={{ width: 110 }}>
                      <option value="months">Months</option>
                      <option value="years">Years</option>
                    </select>
                  </>
                )}
                <label className="bill-warranty-check">
                  <input type="checkbox" checked={it.serialNumberYes} onChange={e => updateItem(i, 'serialNumberYes', e.target.checked)} />
                  Serial Number
                </label>
                {it.serialNumberYes && (
                  <input className="ap-input" value={it.serialNumber} onChange={e => updateItem(i, 'serialNumber', e.target.value)} placeholder="e.g. SN1234567890" style={{ width: 170 }} />
                )}
                {it.price && it.quantity && (
                  <span className="bill-item-subtotal">= {fmtINR(Number(it.quantity) * Number(it.price))}</span>
                )}
              </div>
            </div>
          ))}

          <button type="button" className="ap-btn ap-btn-ghost" style={{ fontSize: '0.84rem', display: 'flex', alignItems: 'center', gap: 6 }} onClick={addItem}>
            <Plus size={15} /> Add Item
          </button>

          <div className="bill-total-bar">
            <span>Total</span>
            <span className="bill-total-amount">{fmtINR(total)}</span>
          </div>

          <div className="ap-btn-row">
            <button type="submit" className="ap-btn ap-btn-primary" disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <Receipt size={16} />
              {saving ? 'Generating…' : 'Generate & Save Bill'}
            </button>
            <button type="button" className="ap-btn ap-btn-ghost" onClick={() => navigate('/admin/dashboard')} disabled={saving}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}
